import React, { useContext, useEffect, useState } from "react";
import { useBasket } from "../basketContext";
import { AuthContext } from "../AuthContext.js";
import { useNavigate } from "react-router-dom";
import formatCurrency from "../utility/formatCurrency";
import { trackBeginCheckout, trackRemoveFromCart } from "../utility/analytics";
import { TrashIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Button from "./Button";

const CHECKOUT_API_URL = (
  process.env.REACT_APP_API_URL || "https://api.createprints.ai/api"
).replace(/\/+$/, "");

function toCheckoutItem(item) {
  const [jobIdFromId = "", variantIdFromId = ""] = String(item.id || "").split("/");
  const jobId = item.generationJobId || jobIdFromId;
  const productVariantId = item.variantId || variantIdFromId;
  const frameOptionId = item.frameOptionId ?? item.frame;
  const quantity = Number(item.quantity ?? 1);

  if (!jobId || !productVariantId) {
    return null;
  }

  const checkoutItem = {
    jobId,
    productVariantId,
    quantity: Number.isInteger(quantity) && quantity > 0 ? quantity : 1,
  };

  if (frameOptionId != null && frameOptionId !== "") {
    checkoutItem.frameOptionId = frameOptionId;
  }

  return checkoutItem;
}

const Basket = (props) => {
  const { handleClose } = props;
  const { basketItems, removeItem } = useBasket();
  const { session } = useContext(AuthContext);
  const [subtotal, setSubtotal] = useState(0);
  const [checkoutError, setCheckoutError] = useState("");
  const [checkingOut, setCheckingOut] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    calculateSubtotal();
  }, [basketItems]);

  const handleRemoveItem = (itemId) => {
    const removed = basketItems.find((item) => item.id === itemId);
    if (removed) trackRemoveFromCart(removed);
    removeItem(itemId);
  };

  const calculateSubtotal = () => {
    const amount = basketItems.reduce((total, item) => {
      return total + item.price * item.quantity;
    }, 0);
    setSubtotal(amount);
  };

  const handleSubmit = async () => {
    setCheckoutError("");

    if (!session?.access_token) {
      handleClose();
      navigate("/login", {
        state: {
          message: "Please log in to proceed to checkout",
          redirectUrl: "/checkout",
        },
      });
      return;
    }

    const items = basketItems.map(toCheckoutItem).filter(Boolean);
    if (items.length !== basketItems.length) {
      setCheckoutError(
        "Some basket items are missing print details. Remove them and add the prints again."
      );
      return;
    }

    setCheckingOut(true);
    try {
      const response = await fetch(`${CHECKOUT_API_URL}/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          "Idempotency-Key": `web-basket-${Date.now()}`,
        },
        body: JSON.stringify({
          source: "web",
          country: "GB",
          items,
        }),
      });

      const sessionData = await response.json().catch(() => ({}));
      const checkoutUrl = sessionData.checkoutUrl || sessionData.url;

      if (!response.ok) {
        setCheckoutError(
          sessionData.error ||
            sessionData.message ||
            "Checkout could not be started. Please try again."
        );
        return;
      }

      if (!checkoutUrl || typeof checkoutUrl !== "string") {
        setCheckoutError("Checkout link was not returned. Please try again.");
        return;
      }

      trackBeginCheckout(basketItems);
      window.location.assign(checkoutUrl);
    } catch (error) {
      console.error("Error during checkout:", error);
      setCheckoutError("Checkout could not be started. Please try again.");
    } finally {
      setCheckingOut(false);
    }
  };

  return (
    <div className="m-4 text-slate-900 flex flex-col justify-between h-full">
      <header className="flex justify-between items-center">
        <p className="text-2xl font-medium">Basket</p>
        <button
          type="button"
          className="rounded-full bg-white p-2 text-slate-900 hover:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          onClick={handleClose}
        >
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </header>
      {basketItems.length > 0 ? (
        <>
          <div className="my-6 divide-y-2 h-full">
            {basketItems.map((item) => (
              <div
                key={item.id}
                className="flex justify-between items-center py-4"
              >
                <div>
                  <div className="flex flex-row items-center">
                    <img
                      src={item.image}
                      className="object-cover h-12 w-12 rounded-lg mr-4"
                    />
                    <div className="flex flex-col">
                      <div className="text-base font-medium">{item.title}</div>
                      <div>{formatCurrency(item.price)}</div>
                      <div>Quantity: {item.quantity}</div>
                    </div>
                  </div>
                </div>
                <button
                  className="p-4"
                  onClick={() => handleRemoveItem(item.id)}
                >
                  <TrashIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
          <footer className="py-8">
            <div className="my-4">
              <p size="lg"
                fullWidth>
                Subtotal: {formatCurrency(subtotal)}
              </p>
              <p className="text-sm text-slate-400">Delivery calculated at checkout.</p>
            </div>
            {checkoutError ? (
              <p className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {checkoutError}
              </p>
            ) : null}
            <div>
              <Button
                onClick={handleSubmit}
                size="lg"
                fullWidth
                disabled={checkingOut}
                loading={checkingOut}
              >
                {checkingOut ? "Starting checkout..." : "Proceed to Checkout"}
              </Button>
            </div>
          </footer>
        </>
      ) : (
        <div className="my-6 h-full">Your basket is empty</div>
      )}
    </div>
  );
};

export default Basket;
